import { uuidv7 } from "uuidv7";
import { datasetFormRequest } from "./dataset-form-signal";
import { notify } from "../notifications/NotificationProvider";
import { adminRpc } from "../../services/admin-rpc-client";
import { errorMessage } from "../../utils/error-message";

// Shared monitor provisioning — the create/edit row-graph orchestration that every monitor-kind form
// (pinger / cdc / system-monitor) ran identically. The three submit*Form functions differed ONLY in the
// values they wrote (job name/kind/description/command, the monitor's checker+thresholds, the schedule
// expr string, and the success messages). Each form now builds those per-kind values and calls
// provisionMonitor; the create-vs-edit branching, the 6 firewall writes in dependency order, the guards,
// the error handling, and the UID minting live here once.
//
// The graph (a monitor's runtime parts):
//   monitor       — the policy row (checker, thresholds in extra_data, armed dead-man's switch)
//   job           — workeruid + monitor_uid + the runDirect collector command (payload_json)
//   job_schedule  — the timing that fires the job
//   circuitbreaker— pre-created green so the dashboard shows green immediately (runtime also creates lazily)

// newUid mints a uid32 (uuidv7 with the dashes stripped) — the id shape the continuum uid columns expect.
export function newUid(): string {
  return uuidv7().replaceAll("-", "");
}

export interface ProvisionMonitorParams {
  // edit when both present; create otherwise.
  existingMonitorUid: string | undefined;
  existing: { monitor: Record<string, unknown>; job: Record<string, unknown> | undefined } | undefined;

  enabled: boolean;
  worker: string;

  // The monitor row's mutable fields (plugin_fileset_uid, plugin_name, channel_uids, interval/grace,
  // alert_on_overdue, extra_data). Built per-kind by the caller.
  monitorValues: Record<string, unknown>;

  // The job row's per-kind fields.
  jobName: string;
  jobKind: string; // "monitor-probe" (pinger/cdc) | "scheduled" (system-monitor)
  jobDescription: string;
  jobFilesetUid?: string; // system-monitor sets fileset_uid; pinger/cdc don't
  payloadJson: string; // JSON: { jobName, command, runDirect }

  // The schedule row.
  scheduleName: string;
  scheduleDescription: string;
  scheduleExpr: string; // already-formatted ("every N seconds" / "every N minutes" — unit is per-kind)

  // Notifications.
  createdMessage: string;
  updatedMessage: string;
}

function failProvision(message: string) {
  notify({ severity: "error", title: "Save failed", message });
}

// provisionMonitor runs the create or edit row-graph through the firewall (as the ContinuumAdmin caller).
// Returns nothing; surfaces success/failure via notify and closes the form on success. The caller does
// its own input validation (required worker/channel/target) before calling.
export async function provisionMonitor(p: ProvisionMonitorParams): Promise<void> {
  try {
    if (p.existingMonitorUid && p.existing) {
      // ---- EDIT: reconcile the existing graph ----
      const jobUid = p.existing.job?.["uid"] as string | undefined;
      const upMon = await adminRpc.update("monitor", { uid: p.existingMonitorUid }, p.monitorValues);
      if (!upMon.success) throw new Error(upMon.error?.message || "update monitor failed");

      if (jobUid) {
        // the name is the monitor's to change (it names the job, its schedule and the alert title)
        const upJob = await adminRpc.update("job", { uid: jobUid }, {
          name: p.jobName,
          description: p.jobDescription,
          workeruid: p.worker,
          payload_json: p.payloadJson,
        });
        if (!upJob.success) throw new Error(upJob.error?.message || "update job failed");
        const upSched = await adminRpc.update("job_schedule", { job_uid: jobUid }, {
          name: p.scheduleName,
          description: p.scheduleDescription,
          schedule_expr: p.scheduleExpr,
          enabled: p.enabled,
        });
        if (!upSched.success) throw new Error(upSched.error?.message || "update job_schedule failed");
      }
      notify({ severity: "success", title: "Monitor updated", message: p.updatedMessage });
    } else {
      // ---- CREATE: provision the whole graph (leaf rows first, link last) ----
      const monitorUid = newUid();
      const jobUid = newUid();
      const scheduleUid = newUid();
      const breakerUid = newUid();

      const insMon = await adminRpc.insert("monitor", { uid: monitorUid, ...p.monitorValues });
      if (!insMon.success) throw new Error(insMon.error?.message || "insert monitor failed");

      const insJob = await adminRpc.insert("job", {
        uid: jobUid,
        name: p.jobName,
        workeruid: p.worker,
        workerenabled: true,
        kind: p.jobKind,
        description: p.jobDescription,
        concurrency_policy: "wait", // a slow probe must NOT stack on the next tick
        // Required when concurrency_policy='wait' (scheduler/engine.go SKIPS the schedule otherwise).
        wait_completion_policy: "next_tick",
        monitor_uid: monitorUid,
        ...(p.jobFilesetUid ? { fileset_uid: p.jobFilesetUid } : {}),
        payload_json: p.payloadJson,
        max_attempts: 1, // a missed tick is the NEXT tick's job, not a retry of this one
      });
      if (!insJob.success) throw new Error(insJob.error?.message || "insert job failed");

      const insSched = await adminRpc.insert("job_schedule", {
        uid: scheduleUid,
        name: p.scheduleName,
        description: p.scheduleDescription,
        job_uid: jobUid,
        schedule_expr: p.scheduleExpr,
        enabled: p.enabled,
      });
      if (!insSched.success) throw new Error(insSched.error?.message || "insert job_schedule failed");

      // Pre-create the breaker green so the dashboard shows green immediately. Upsert by job_uid (unique).
      const insBreaker = await adminRpc.upsert("circuitbreaker", ["job_uid"], {
        uid: breakerUid,
        job_uid: jobUid,
        breaker_status: "green",
        consecutive_failures: 0,
      });
      if (!insBreaker.success) throw new Error(insBreaker.error?.message || "create circuitbreaker failed");

      notify({ severity: "success", title: "Monitor created", message: p.createdMessage });
    }
    datasetFormRequest.next(undefined);
  } catch (err) {
    failProvision(errorMessage(err) || "Failed to save the monitor.");
  }
}
